import { Errors, useAuth, ServerErrorSummary, ServerErrorSortKey } from "@app/shared";
import { useDialog } from "../../../hooks";
import { DataInfoCard, Icons, Link, PageContainer, SortableTable, ServerSideOptions } from "../../shared";
import { FilterOption } from "../../shared/Filter";
import { format } from "date-fns";
import { useCallback, useEffect, useState, useMemo } from "react";
import { toastWarning, debounce } from "../../../helpers";
import JsonViewer from "../../shared/JsonViewer";

type MappedServerErrorSummary = ServerErrorSummary & { id: string };

type ServerErrorsQuery = {
    page: number;
    pageSize: number;
    sortKey?: ServerErrorSortKey;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<MappedServerErrorSummary>[];
};

export const ServerErrorsTable: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<ServerErrorSummary[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [totalOccurrences, setTotalOccurrences] = useState(0);
    const [totalHourlyRate, setTotalHourlyRate] = useState(0);
    const [query, setQuery] = useState<ServerErrorsQuery>({
        page: 1,
        pageSize: 15,
        sortKey: ServerErrorSortKey.LastOccurrence,
        sortDirection: "desc",
    });

    const { numberFormatter } = useAuth();
    const confirm = useDialog();

    const convertFiltersToParams = (filters?: FilterOption<MappedServerErrorSummary>[]) => {
        const params: {
            traceId?: string;
            dateFrom?: Date;
            dateTo?: Date;
        } = {};

        if (!filters) return params;

        filters.forEach((filter) => {
            if (filter.value !== undefined && filter.value !== "") {
                switch (filter.id) {
                    case "traceId":
                        params.traceId = filter.value;
                        break;
                    case "lastOccurrence":
                        if (filter.value && typeof filter.value === "object") {
                            if (filter.value.from) params.dateFrom = new Date(filter.value.from);
                            if (filter.value.to) params.dateTo = new Date(filter.value.to);
                        }
                        break;
                }
            }
        });

        return params;
    };

    const fetchErrors = useCallback(async () => {
        setIsLoading(true);
        try {
            const filterParams = convertFiltersToParams(query.filters);

            const response = await Errors.getServerErrorsPaginated(query.page, query.pageSize, query.sortKey, query.sortDirection, filterParams.traceId, filterParams.dateFrom, filterParams.dateTo);

            setServerErrors(response?.data || []);
            setTotalCount(response?.totalCount || 0);
            setTotalOccurrences(response?.totalOccurrences || 0);
            setTotalHourlyRate(response?.totalHourlyRate || 0);
        } catch (error) {
            console.error("Failed to fetch server errors:", error);
            toastWarning("Failed to load server errors. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, [query]);

    const debouncedFetchErrors = useMemo(() => debounce(fetchErrors, 300), [fetchErrors]);

    useEffect(() => {
        debouncedFetchErrors();
    }, [debouncedFetchErrors]);

    const handleSort = (sortKey: string, direction: "asc" | "desc") => {
        // Map dataKey to enum value
        const sortKeyEnum = sortKey === "lastOccurrence" ? ServerErrorSortKey.LastOccurrence
            : sortKey === "traceId" ? ServerErrorSortKey.TraceId
            : sortKey === "total" ? ServerErrorSortKey.Total
            : sortKey === "hourlyAverage" ? ServerErrorSortKey.HourlyAverage
            : ServerErrorSortKey.LastOccurrence;

        setQuery((prev) => ({
            ...prev,
            sortKey: sortKeyEnum,
            sortDirection: direction,
            page: 1,
        }));
    };

    const handleFilter = (filters: FilterOption<MappedServerErrorSummary>[]) => {
        setQuery((prev) => ({
            ...prev,
            filters,
            page: 1,
        }));
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setQuery((prev) => ({
            ...prev,
            page,
            pageSize,
        }));
    };

    const getSortKeyDataKey = (sortKey?: ServerErrorSortKey): string | undefined => {
        switch (sortKey) {
            case ServerErrorSortKey.LastOccurrence:
                return "lastOccurrence";
            case ServerErrorSortKey.TraceId:
                return "traceId";
            case ServerErrorSortKey.Total:
                return "total";
            case ServerErrorSortKey.HourlyAverage:
                return "hourlyAverage";
            default:
                return undefined;
        }
    };

    const serverSideOptions: ServerSideOptions<MappedServerErrorSummary> = {
        enabled: true,
        onSort: handleSort,
        onFilter: handleFilter,
        onPageChange: handlePageChange,
        totalCount,
        currentPage: query.page,
        pageSize: query.pageSize,
        sortKey: getSortKeyDataKey(query.sortKey),
        sortDirection: query.sortDirection,
    };

    const handleDetails = (stack: string) => {
        confirm.show("Stack Trace", "OK", stack, (_) => { });
    };

    const mostRecent = useMemo(() => {
        if (!serverErrors?.length) return null;

        return serverErrors.reduce((latest, current) => {
            const currentDate = new Date(current.lastOccurrence!);
            return currentDate > new Date(latest.lastOccurrence!) ? current : latest;
        });
    }, [serverErrors]);

    return (
        <>
            {(serverErrors?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-4">
                    <DataInfoCard Icon={Icons.Filter} title="Last Error">
                        {mostRecent ? format(mostRecent!.lastOccurrence!, "MMM d  HH:mm") : ""}
                    </DataInfoCard>
                    <DataInfoCard Icon={Icons.Expand} title="Occurrences">
                        {numberFormatter.format(totalOccurrences)}
                    </DataInfoCard>
                    <DataInfoCard Icon={Icons.Success} title="Hourly Rate">
                        {numberFormatter.format(totalHourlyRate)}
                    </DataInfoCard>
                </div>
            )}

            <PageContainer title="Recent Errors">
                <SortableTable
                    isLoading={isLoading}
                    columns={[
                        {
                            header: "Trace Id",
                            dataKey: "traceId",
                            width: 140,
                            renderFunc: (data) => <div className="w-100 break-all">{data?.traceId}</div>,
                            filterable: true,
                            filterType: "text",
                        },
                        {
                            header: "Last Occurrence",
                            dataKey: "lastOccurrence",
                            renderFunc: (data) => <div className="w-100 break-words">{data?.lastOccurrence ? format(data?.lastOccurrence!, "MMM d, yyyy HH:mm:ss") : ""}</div>,
                            width: 100,
                            filterable: true,
                            filterType: "daterange",
                        },
                        {
                            header: "Message",
                            dataKey: "details",
                            renderFunc: (data) => {
                                try {
                                    var jsonObj = data?.details;
                                    if (typeof jsonObj === "string") {
                                        jsonObj = JSON.parse(jsonObj as string);
                                    }
                                    return <div className="w-300 break-words"><div className="max-h-[10em] overflow-auto">{(jsonObj as any)?.RenderedMessage}</div></div>;
                                } catch (e) {
                                    console.error("Failed to parse error details:", e);
                                }
                            },
                            width: 300,
                        },
                        {
                            header: "Errors per Hour",
                            dataKey: "hourlyAverage",
                            width: 100,
                        },
                        {
                            header: "Total",
                            dataKey: "total",
                            width: 100,
                        },
                        {
                            header: "",
                            renderFunc: (fe) => (fe?.details?.length ? <Link onClick={() => handleDetails(fe.details!)}>View Details</Link> : ""),
                            width: 50,
                        },
                    ]}
                    data={serverErrors?.map((de) => ({ ...de, id: de.traceId }))}
                    pagination
                    defaultPageSize={15}
                    pageSizeOptions={[10, 15, 25, 50, 100]}
                    filtering
                    serverSide={serverSideOptions}
                />
            </PageContainer>
            {confirm.renderDialog(
                (stack) => {
                    if (!stack) {
                        return <></>;
                    }
                    var jsonObj = stack;
                    if (typeof stack === "string") {
                        jsonObj = JSON.parse(stack as string);
                    }
                    return (
                        <div className="h-[65vh] overflow-y-auto pb-5">
                            <JsonViewer data={jsonObj} />
                        </div>
                    );
                },
                { className: "w-[75%] h-[75%] flex flex-col overflow-hidden", closeOnOutsideClick: true, showOkButton: false, showCancelButton: false }
            )}
        </>
    );
};
